import {createAsyncThunk} from '@reduxjs/toolkit'
import {
  CreateGradeRequest,
  UpdateGradeRequest,
  FetchGradesRequest,
  CreateTabulationRequest,
  UpdateTabulationRequest,
  SubmitTabulationRequest,
  FetchTabulationsRequest,
  ReviewTabulationRequest,
  DeleteTabulationRequest,
  FetchBulletinRequest,
  CreateGradeResponse,
  UpdateGradeResponse,
  FetchGradesResponse,
  CreateTabulationResponse,
  UpdateTabulationResponse,
  SubmitTabulationResponse,
  FetchTabulationsResponse,
  ReviewTabulationResponse,
  DeleteTabulationResponse,
  FetchBulletinResponse,
  FetchBulletinsResponse,
  MissingGradesResponse,
  FetchSubmissionDetailsResponse,
  FetchStudentGradesRequest,
  FetchStudentGradesResponse,
} from './types'
import {AxiosError} from 'axios'
import axios from 'axios'
import {
  createGradeUrl,
  updateGradeUrl,
  deleteGradeUrl,
  approveGradeUrl,
  rejectGradeUrl,
  fetchGradesUrl,
  fetchGradeByIdUrl,
  createTabulationUrl,
  updateTabulationUrl,
  deleteTabulationUrl,
  submitTabulationUrl,
  fetchTabulationsUrl,
  fetchTabulationByIdUrl,
  fetchApprovalQueueUrl,
  fetchSubmissionDetailsUrl,
  detectMissingGradesUrl,
  reviewTabulationUrl,
  fetchBulletinUrl,
  fetchBulletinsUrl,
  fetchStudentGradesUrl,
} from '../../api'

/**
 * Error handler for grades API
 */
const handleServiceError = (error: any) => {
  const err = error as AxiosError<any>
  let message = 'An unknown error occurred'

  if (err.response) {
    message =
      err.response.data?.fr ||
      err.response.data?.error ||
      err.response.data?.message ||
      err.message
  } else if (err.request) {
    message = 'No response from the server. Please check your internet connection.'
  } else {
    message = err.message || 'An unknown error occurred'
  }

  return message
}

// ==================== GRADE ENDPOINTS ====================

export const createGrade = createAsyncThunk(
  'grades/createGrade',
  async (payload: CreateGradeRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(createGradeUrl, payload)
      return {
        success: true,
        grade: res.data.grade,
        message: res.data.message,
      } as CreateGradeResponse
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const updateGrade = createAsyncThunk(
  'grades/updateGrade',
  async (payload: UpdateGradeRequest, {rejectWithValue}) => {
    try {
      const res = await axios.patch(`${updateGradeUrl}/${payload.id}`, {
        term: payload.term,
        type: payload.type,
        value: payload.value,
        notes: payload.notes,
      })
      return {
        success: true,
        grade: res.data.grade,
        message: res.data.message,
      } as UpdateGradeResponse
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const deleteGrade = createAsyncThunk(
  'grades/deleteGrade',
  async (id: string, {rejectWithValue}) => {
    try {
      await axios.delete(`${deleteGradeUrl}/${id}`)
      return {success: true, id}
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const approveGrade = createAsyncThunk(
  'grades/approveGrade',
  async (id: string, {rejectWithValue}) => {
    try {
      const res = await axios.patch(`${approveGradeUrl}/${id}/approve`)
      return {
        success: true,
        grade: res.data.grade,
        message: res.data.message,
      }
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const rejectGrade = createAsyncThunk(
  'grades/rejectGrade',
  async (payload: {id: string; rejectionReason: string}, {rejectWithValue}) => {
    try {
      const res = await axios.patch(`${rejectGradeUrl}/${payload.id}/reject`, {
        rejectionReason: payload.rejectionReason,
      })
      return {
        success: true,
        grade: res.data.grade,
        message: res.data.message,
      }
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const fetchGrades = createAsyncThunk(
  'grades/fetchGrades',
  async (payload: FetchGradesRequest = {}, {rejectWithValue}) => {
    try {
      const params = new URLSearchParams()
      if (payload.teacherId) params.append('teacherId', payload.teacherId)
      if (payload.studentId) params.append('studentId', payload.studentId)
      if (payload.classroomId) params.append('classroomId', payload.classroomId)
      if (payload.subjectId) params.append('subjectId', payload.subjectId)
      if (payload.term) params.append('term', payload.term)
      if (payload.isApproved !== undefined) params.append('isApproved', String(payload.isApproved))
      if (payload.pageNumber) params.append('pageNumber', String(payload.pageNumber))
      if (payload.pageCount) params.append('pageCount', String(payload.pageCount))
      if (payload.sortField) params.append('sortField', payload.sortField)
      if (payload.sortOrder) params.append('sortOrder', payload.sortOrder)

      const res = await axios.get(`${fetchGradesUrl}?${params.toString()}`)
      return {
        success: true,
        grades: res.data.grades || [],
        total: res.data.total || 0,
        totalPages: res.data.totalPages || 0,
      } as FetchGradesResponse
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const fetchGradeById = createAsyncThunk(
  'grades/fetchGradeById',
  async (id: string, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${fetchGradeByIdUrl}/${id}`)
      return {
        success: true,
        grade: res.data.grade,
      }
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

// ==================== TABULATION ENDPOINTS ====================

export const createTabulation = createAsyncThunk(
  'grades/createTabulation',
  async (payload: CreateTabulationRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(createTabulationUrl, payload)
      return {
        success: true,
        tabulation: res.data.tabulation,
        gradeCount: res.data.gradeCount || 0,
      } as CreateTabulationResponse
    } catch (error) {
      const err = error as AxiosError<any>
      // Provide better error message for 404 (route not found)
      if (err.response?.status === 404) {
        return rejectWithValue({
          success: false,
          error: 'Backend route not found. The endpoint /api/grades/tabulations/create does not exist on the server. Please ensure the tabulation routes are configured in the backend.',
        })
      }
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const updateTabulation = createAsyncThunk(
  'grades/updateTabulation',
  async (payload: UpdateTabulationRequest, {rejectWithValue}) => {
    try {
      // Use POST with id in body (workaround for CORS issue - PATCH method not allowed)
      const res = await axios.post(updateTabulationUrl, {
        id: payload.id,
        grades: payload.grades,
      })
      return {
        success: true,
        gradeCount: res.data.gradeCount || 0,
      } as UpdateTabulationResponse
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const deleteTabulation = createAsyncThunk(
  'grades/deleteTabulation',
  async (payload: DeleteTabulationRequest, {rejectWithValue}) => {
    try {
      // Use POST with id in body (workaround for CORS issue - DELETE method not allowed)
      const res = await axios.post(deleteTabulationUrl, {id: payload.id})
      return {
        success: true,
        message: res.data.message,
      } as DeleteTabulationResponse
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const submitTabulation = createAsyncThunk(
  'grades/submitTabulation',
  async (payload: SubmitTabulationRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(`${submitTabulationUrl}/${payload.id}/submit`, {
        overrideMissingGrades: payload.overrideMissingGrades,
        overrideReason: payload.overrideReason,
      })

      // Handle missing grades error (409 Conflict)
      if (res.status === 409 || res.data.missingGrades) {
        return {
          success: false,
          status: 409,
          message: res.data.message,
          missingCount: res.data.missingCount,
          missingGrades: res.data.missingGrades,
          summary: res.data.summary,
        } as any
      }

      return {
        success: true,
        submissionId: res.data.submissionId,
        message: res.data.message,
      } as SubmitTabulationResponse
    } catch (error) {
      const err = error as AxiosError<any>
      // Handle 409 Conflict (missing grades)
      if (err.response?.status === 409) {
        return {
          success: false,
          status: 409,
          message: err.response.data?.message,
          missingCount: err.response.data?.missingCount,
          missingGrades: err.response.data?.missingGrades,
          summary: err.response.data?.summary,
        } as any
      }
      
      // Extract error message from response
      const errorData = err.response?.data || {}
      const errorMessage = errorData?.fr || errorData?.message || errorData?.error || handleServiceError(error)
      
      console.error('❌ Submit tabulation error:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        error: err.message,
      })
      
      return rejectWithValue({
        success: false,
        message: errorMessage,
        error: errorData?.error || handleServiceError(error),
        status: err.response?.status,
        data: err.response?.data,
      })
    }
  }
)

export const fetchTabulations = createAsyncThunk(
  'grades/fetchTabulations',
  async (params: FetchTabulationsRequest = {}, {rejectWithValue}) => {
    try {
      // Create an object containing only valid values
      // Filter out undefined, null, empty strings, and the string 'undefined'
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => 
          value !== undefined && 
          value !== null && 
          value !== '' && 
          value !== 'undefined' &&
          String(value).trim() !== ''
        )
      ) as FetchTabulationsRequest

      // Ensure status is normalized (backend maps "pending" to "pending_approval", but let's be explicit)
      // Note: TypeScript type doesn't include 'pending', but handle it for safety
      if (cleanParams.status && String(cleanParams.status).toLowerCase() === 'pending') {
        cleanParams.status = 'pending_approval'
      }

      const res = await axios.get(fetchTabulationsUrl, {params: cleanParams})
      
      return {
        success: true,
        tabulations: res.data.tabulations || [],
        total: res.data.total || 0,
        totalPages: res.data.totalPages || 0,
        pagination: res.data.pagination,
      } as FetchTabulationsResponse
    } catch (err: any) {
      console.error('❌ Frontend: Error fetching tabulations:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        config: {
          url: err.config?.url,
          params: err.config?.params,
        }
      })
      const errorData = err.response?.data || {success: false, error: handleServiceError(err)}
      return rejectWithValue(errorData)
    }
  }
)

export const fetchTabulationById = createAsyncThunk(
  'grades/fetchTabulationById',
  async (id: string, {rejectWithValue}) => {
    try {
      // Validate ID before making request
      if (!id || id === 'undefined' || id === '[object Object]') {
        return rejectWithValue({
          success: false,
          error: 'Invalid tabulation ID',
        })
      }

      // Backend route order has been fixed, so /api/grades/tabulations/:id should work now
      // The route order is: /:id/update, /:id/submit, /:id/missing-grades, /:id/review, then /:id
      const res = await axios.get(`${fetchTabulationByIdUrl}/${id}`)
      
      return {
        success: true,
        tabulation: res.data.tabulation || res.data,
      }
    } catch (error: any) {
      console.error('❌ Error fetching tabulation by ID:', error)
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const fetchApprovalQueue = createAsyncThunk(
  'grades/fetchApprovalQueue',
  async (payload: FetchTabulationsRequest = {}, {rejectWithValue}) => {
    try {
      const params = new URLSearchParams()
      params.append('pageNumber', String(payload.pageNumber || 1))
      params.append('pageCount', String(payload.pageCount || 10))
      params.append('sortField', payload.sortField || 'submittedAt')
      params.append('sortOrder', payload.sortOrder || 'desc')
      if (payload.search) params.append('search', payload.search)

      const res = await axios.get(`${fetchApprovalQueueUrl}?${params.toString()}`)
      
      return {
        success: true,
        tabulations: res.data.tabulations || res.data.queueItems || [], // Support both response formats
        total: res.data.total || 0,
        totalPages: res.data.totalPages || 0,
      } as FetchTabulationsResponse
    } catch (error) {
      console.error('❌ Admin approval queue error:', error)
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const fetchSubmissionDetails = createAsyncThunk(
  'grades/fetchSubmissionDetails',
  async (submissionId: string, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${fetchSubmissionDetailsUrl}/${submissionId}`)
      return {
        success: true,
        submission: res.data.submission,
      } as FetchSubmissionDetailsResponse
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const detectMissingGrades = createAsyncThunk(
  'grades/detectMissingGrades',
  async (tabulationId: string, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${detectMissingGradesUrl}/${tabulationId}/missing-grades`)
      return {
        success: true,
        ...res.data,
      } as MissingGradesResponse
    } catch (error) {
      return rejectWithValue({
        success: false,
        error: handleServiceError(error),
      })
    }
  }
)

export const reviewTabulation = createAsyncThunk(
  'grades/reviewTabulation',
  async (payload: ReviewTabulationRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(`${reviewTabulationUrl}/${payload.id}/review`, {
        action: payload.action,
        rejectionReason: payload.rejectionReason,
        adminNotes: payload.adminNotes,
      })
      return {
        success: true,
        tabulation: res.data.tabulation,
        message: res.data.message,
        missingGradesWarning: res.data.missingGradesWarning,
      } as ReviewTabulationResponse
    } catch (error) {
      const err = error as AxiosError<any>
      console.error('❌ Error reviewing tabulation:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
      })

      // Extract multilingual error message
      let errorMessage: string = 'Failed to review tabulation'
      const errorData = err.response?.data

      if (errorData) {
        if (errorData.message) {
          if (typeof errorData.message === 'string') {
            errorMessage = errorData.message
          } else if (errorData.message.fr) {
            errorMessage = errorData.message.fr
          } else if (errorData.message.en) {
            errorMessage = errorData.message.en
          } else if (errorData.message.ar) {
            errorMessage = errorData.message.ar
          }
        } else if (errorData.error) {
          errorMessage = errorData.error
        }
      }

      return rejectWithValue({
        success: false,
        message: errorData?.message || errorMessage,
        error: errorMessage,
        status: err.response?.status,
        data: err.response?.data,
      })
    }
  }
)

// ==================== BULLETIN ENDPOINTS ====================

export const fetchBulletin = createAsyncThunk(
  'grades/fetchBulletin',
  async (payload: FetchBulletinRequest, {rejectWithValue}) => {
    try {
      const url = payload.term
        ? `${fetchBulletinUrl}/${payload.studentId}/${payload.term}`
        : `${fetchBulletinsUrl}/${payload.studentId}`
      console.log('📊 Fetching bulletin:', {url, studentId: payload.studentId, term: payload.term})
      const res = await axios.get(url)
      console.log('✅ Bulletin response:', res.data)
      
      // Check if response indicates an error even with 200 status
      if (res.data && res.data.success === false) {
        console.warn('⚠️ API returned success:false in response:', res.data)
        throw new Error(res.data.message || res.data.error || 'Bulletin fetch failed')
      }

      if (payload.term) {
        return {
          success: true,
          bulletin: res.data.bulletin,
          term: payload.term,
        } as FetchBulletinResponse & {term: string}
      } else {
        return {
          success: true,
          bulletins: res.data.bulletins,
          studentId: payload.studentId,
        } as FetchBulletinsResponse & {studentId: string}
      }
    } catch (error) {
      const err = error as AxiosError<any>
      console.error('❌ Bulletin fetch error:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.url,
      })
      
      // Extract error message from response
      let errorMessage = 'Failed to fetch bulletin'
      const responseData = err.response?.data
      
      if (responseData) {
        // Try to get message from various possible locations
        if (responseData.message) {
          if (typeof responseData.message === 'string') {
            errorMessage = responseData.message
          } else if (responseData.message.fr) {
            errorMessage = responseData.message.fr
          } else if (responseData.message.en) {
            errorMessage = responseData.message.en
          } else if (responseData.message.ar) {
            errorMessage = responseData.message.ar
          } else if (typeof responseData.message === 'object') {
            // Get first available language
            errorMessage = Object.values(responseData.message)[0] as string || errorMessage
          }
        } else if (responseData.error) {
          if (typeof responseData.error === 'string') {
            errorMessage = responseData.error
          } else if (responseData.error.message) {
            errorMessage = responseData.error.message
          } else if (responseData.error.fr) {
            errorMessage = responseData.error.fr
          } else if (responseData.error.en) {
            errorMessage = responseData.error.en
          }
        }
      }
      
      // Fallback to handleServiceError if no message found
      if (errorMessage === 'Failed to fetch bulletin') {
        errorMessage = handleServiceError(error)
      }
      
      return rejectWithValue({
        success: false,
        error: errorMessage,
        message: responseData?.message,
        status: err.response?.status,
        data: responseData,
      })
    }
  }
)

// Fetch student grades directly from approved tabulations (alternative to bulletin)
// This works even when class level is not defined
export const fetchStudentGrades = createAsyncThunk(
  'grades/fetchStudentGrades',
  async (payload: FetchStudentGradesRequest, {rejectWithValue}) => {
    try {
      const url = fetchStudentGradesUrl(payload.studentId)
      const res = await axios.get(url)
      
      if (res.data && res.data.success === false) {
        console.warn('⚠️ API returned success:false in response:', res.data)
        throw new Error(res.data.message || res.data.error || 'Student grades fetch failed')
      }

      return {
        success: true,
        grades: res.data.grades || [],
        student: res.data.student,
      } as FetchStudentGradesResponse
    } catch (error) {
      const err = error as AxiosError<any>
      console.error('❌ Student grades fetch error:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        url: err.config?.url,
      })
      
      let errorMessage = 'Failed to fetch student grades'
      const responseData = err.response?.data
      
      if (responseData) {
        if (responseData.message) {
          if (typeof responseData.message === 'string') {
            errorMessage = responseData.message
          } else if (responseData.message.fr) {
            errorMessage = responseData.message.fr
          } else if (responseData.message.en) {
            errorMessage = responseData.message.en
          } else if (responseData.message.ar) {
            errorMessage = responseData.message.ar
          }
        } else if (responseData.error) {
          if (typeof responseData.error === 'string') {
            errorMessage = responseData.error
          } else if (responseData.error.fr) {
            errorMessage = responseData.error.fr
          } else if (responseData.error.en) {
            errorMessage = responseData.error.en
          } else if (responseData.error.ar) {
            errorMessage = responseData.error.ar
          }
        }
      }
      
      return rejectWithValue({
        success: false,
        error: errorMessage,
        message: err.response?.data?.message || errorMessage,
        status: err.response?.status,
        data: err.response?.data,
      })
    }
  }
)

